import { useEffect, useState, type ReactNode } from "react";
import { ChevronDownIcon, PawPrintIcon, PanelLeftCloseIcon, MenuIcon } from "./icons";

/** Below this width the sidebar auto-collapses to the icon rail (like the Jmix drawer on small screens). */
const COLLAPSE_BREAKPOINT = "(max-width: 900px)";
/**
 * Wanneer wordt de zijbalk een lade die over de inhoud schuift, met een knop in de kopbalk?
 *
 * Twee gevallen, en het tweede is de reden dat dit geen enkele breedte is:
 *
 *   • een telefoon (onder 768 pixels) — daar past geen kolom naast het werk, met muis of zonder;
 *   • een aanraakscherm tot 1024 pixels — de tablet. Die is breder dan een telefoon, maar de
 *     schil klapte daar terug op de icoonrail van 72 pixels: navigatie zonder woorden, terwijl er
 *     ruimte zat. Staand op een iPad blijft er dan 738 pixels werk over met een menu dat je niet
 *     kunt lezen; liggend eet de volle zijbalk juist een derde van het scherm.
 *
 * Het aanraak-deel staat er met opzet bij: een desktopvenster dat toevallig 900 pixels breed is,
 * houdt de rail die het altijd had. `pointer: coarse` kijkt naar de primaire invoer, dus een
 * laptop met muis én touchscreen valt er niet onder.
 */
const MOBILE_BREAKPOINT = "(max-width: 768px), (pointer: coarse) and (max-width: 1024px)";

export interface AppShellProps {
  /** Brand/app name shown next to the logo at the top of the sidebar. */
  brandTitle: string;
  /** Optional brand logo (url) rendered as the circular paw mark. */
  brandLogoSrc?: string;
  /**
   * Optioneel woordmerk: teken én naam in één afbeelding, voor de uitgeklapte zijbalk.
   *
   * <p>Staat dit er, dan vervangt het het merkteken plus de losse titel — die twee naast elkaar
   * zetten terwijl het woordmerk de naam al draagt geeft "docasoft Docasoft". Ingeklapt valt het
   * woordmerk weg en blijft het teken staan; op een rail van 60 pixels past geen naam.
   *
   * <p>De titel blijft verplicht: hij is het toegankelijke label van de zijbalk, ook als er alleen
   * een plaatje te zien is.
   */
  brandWordmarkSrc?: string;
  /** Current view title — the header h1. */
  title: string;
  /** Optional header slot to the right of the title (e.g. a global search field). */
  header?: ReactNode;
  /** Optional card pinned under the brand (e.g. the {@link LocationSelector} / vestiging card). */
  locationSelector?: ReactNode;
  /** Sidebar navigation — compose {@link NavSection} + {@link NavItem}. */
  nav: ReactNode;
  /** Optional pinned bottom-of-sidebar content (e.g. a {@link ProfileCard}). */
  sidebarFooter?: ReactNode;
  /**
   * Render the content area at the denser 80% scale. Parity: on acc the not-yet-migrated detail/form
   * views (animal detail, relaties, product detail, settings) are still the Vaadin shell at 80%, while
   * the migrated overview modules render at 100%. This replicates that per-screen density.
   */
  contentDense?: boolean;
  /** The working area. */
  children: ReactNode;
}

/**
 * jve-2-6: the application shell — left sidebar (brand · location · nav · footer) + top header
 * (view title · header slot) + working area. Reproduces the Jmix `MainView` AppLayout structure on
 * the new design system (token-styled, theme-aware). The sidebar collapses to an icon-only rail via
 * the single toggle in the sidebar brand row — it stays on the rail (mirrored) so the collapsed
 * sidebar can always be reopened from the same spot. Extraction-ready.
 */
export function AppShell({
  brandTitle,
  brandLogoSrc,
  brandWordmarkSrc,
  title,
  header,
  locationSelector,
  nav,
  sidebarFooter,
  contentDense,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [ladeOpen, setLadeOpen] = useState(false);
  const toggle = () => setCollapsed((c) => !c);

  // Auto-collapse to the icon rail on small screens (the Jmix drawer collapses there too). A manual
  // toggle still works between viewport changes. Guarded for jsdom/SSR (no matchMedia).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(COLLAPSE_BREAKPOINT);
    const smal = window.matchMedia(MOBILE_BREAKPOINT);
    // Op de telefoon is de lade juist de volledige zijbalk met labels: een icoonrail naast de
    // inhoud kost daar breedte zonder iets te verduidelijken.
    const apply = () => {
      setMobile(smal.matches);
      setCollapsed(mq.matches && !smal.matches);
      if (!smal.matches) setLadeOpen(false);
    };
    apply();
    mq.addEventListener("change", apply);
    smal.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      smal.removeEventListener("change", apply);
    };
  }, []);

  // Escape sluit de lade, zoals elke overlay in de app.
  useEffect(() => {
    if (!ladeOpen) return;
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLadeOpen(false);
    };
    document.addEventListener("keydown", opToets);
    return () => document.removeEventListener("keydown", opToets);
  }, [ladeOpen]);
  return (
    <div
      className="fui-shell"
      data-collapsed={collapsed ? "" : undefined}
      data-mobiel={mobile ? "" : undefined}
      data-lade-open={mobile && ladeOpen ? "" : undefined}
    >
      <aside className="fui-shell__sidebar">
        <div className="fui-shell__brand">
          <div className="fui-shell__brand-left">
            <span className="fui-shell__brand-logo" aria-hidden="true">
              {brandLogoSrc ? (
                <img className="fui-shell__brand-logo-img" src={brandLogoSrc} alt="" />
              ) : (
                <PawPrintIcon width={22} height={22} />
              )}
            </span>
            {brandWordmarkSrc ? (
              <img className="fui-shell__brand-wordmark" src={brandWordmarkSrc} alt={brandTitle} />
            ) : (
              <span className="fui-shell__brand-title">{brandTitle}</span>
            )}
          </div>
          <button
            type="button"
            className="fui-shell__collapse-toggle"
            aria-label={collapsed ? "Navigatie uitklappen" : "Navigatie inklappen"}
            aria-expanded={!collapsed}
            title={collapsed ? "Uitklappen" : "Inklappen"}
            onClick={toggle}
          >
            <PanelLeftCloseIcon width={18} height={18} />
          </button>
        </div>
        {locationSelector && <div className="fui-shell__location">{locationSelector}</div>}
        <nav
          className="fui-shell__nav"
          aria-label={brandTitle}
          onClick={(e) => {
            if (mobile && (e.target as HTMLElement).closest(".fui-nav-item")) setLadeOpen(false);
          }}
        >
          {nav}
        </nav>
        {sidebarFooter && <div className="fui-shell__sidebar-footer">{sidebarFooter}</div>}
      </aside>
      {mobile && ladeOpen && (
        <button
          type="button"
          className="fui-shell__lade-scherm"
          aria-label="Navigatie sluiten"
          onClick={() => setLadeOpen(false)}
        />
      )}
      <div className="fui-shell__main">
        <header className="fui-shell__header">
          {mobile && (
            <button
              type="button"
              className="fui-shell__lade-knop"
              aria-label={ladeOpen ? "Navigatie sluiten" : "Navigatie openen"}
              aria-expanded={ladeOpen}
              onClick={() => setLadeOpen((o) => !o)}
            >
              <MenuIcon width={20} height={20} />
            </button>
          )}
          <h1 className="fui-shell__title">{title}</h1>
          {header && <div className="fui-shell__header-slot">{header}</div>}
        </header>
        <main className={"fui-shell__content" + (contentDense ? " fui-shell__content--dense" : "")}>
          {children}
        </main>
      </div>
    </div>
  );
}

export interface NavSectionProps {
  /** Section label. Rendered as an uppercase static label, or a collapsible summary (see below). */
  label: string;
  /**
   * Header treatment, mirroring the Jmix sidebar groups:
   *   • omitted        → a static uppercase label, always open (the Jmix "APPLICATION" group);
   *   • `true`/`false` → a collapsible accordion (Title-case summary + leading chevron) whose
   *                      initial open state is this value — `menu.xml`'s `opened` flag.
   */
  defaultOpen?: boolean;
  /** Optional section glyph shown left of a collapsible summary (the Jmix Security/Data-Tools icon). */
  icon?: ReactNode;
  children: ReactNode;
}

/** A labelled group of {@link NavItem}s in the sidebar. */
export function NavSection({ label, defaultOpen, icon, children }: NavSectionProps) {
  const collapsible = defaultOpen !== undefined;
  const [open, setOpen] = useState(defaultOpen ?? true);

  if (!collapsible) {
    return (
      <div className="fui-nav-section">
        <div className="fui-nav-section__label">{label}</div>
        {children}
      </div>
    );
  }

  return (
    <div
      className="fui-nav-section"
      data-collapsible=""
      data-open={open ? "" : undefined}
      data-has-icon={icon ? "" : undefined}
    >
      <button
        type="button"
        className="fui-nav-section__summary"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDownIcon className="fui-nav-section__chevron" width={16} height={16} />
        {icon && (
          <span className="fui-nav-section__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="fui-nav-section__summary-text">{label}</span>
      </button>
      {/* Items stay in the DOM (display toggled by CSS) so the collapsed icon-rail can still show
          every glyph regardless of a section's open state. */}
      <div className="fui-nav-section__items">{children}</div>
    </div>
  );
}

export interface NavItemProps {
  label: string;
  active?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  /**
   * When set, the item renders as a link (full-page navigation) instead of a state-nav button — used
   * for not-yet-migrated screens that hand off to the legacy Jmix app at the site root.
   */
  href?: string;
  /** Marks a hand-off to the legacy Jmix app (adds a marker + tooltip). Pair with {@link href}. */
  legacy?: boolean;
  /** Tooltip override (defaults to the label; legacy items get a "opens in classic Backoffice" hint). */
  title?: string;
  /** Een rood rondje met een aantal achter het label (bijv. nieuwe klantverzoeken); 0 of leeg toont niets. */
  badge?: number;
}

/**
 * A single sidebar navigation entry. Renders as a button for in-SPA (state-based) navigation, or as
 * an anchor when {@link href} is set (a real navigation — used to hand off to the legacy Jmix app).
 */
export function NavItem({ label, active, icon, onClick, href, legacy, title, badge }: NavItemProps) {
  const inner = (
    <>
      {icon && (
        <span className="fui-nav-item__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="fui-nav-item__label">{label}</span>
      {badge ? (
        <span className="fui-nav-item__badge" aria-label={String(badge)}>
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
      {/* The live sidebar marks the active item with a small dot on the right, on top of the
          highlight — see `.sb-item-dot` in frontend-react/sidebar. */}
      {active ? <span className="fui-nav-item__dot" aria-hidden="true" /> : null}
    </>
  );

  if (href) {
    return (
      <a
        className="fui-nav-item"
        href={href}
        data-active={active ? "" : undefined}
        data-legacy={legacy ? "" : undefined}
        aria-current={active ? "page" : undefined}
        title={title ?? label}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      className="fui-nav-item"
      data-active={active ? "" : undefined}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      title={title ?? label}
    >
      {inner}
    </button>
  );
}
